import React from "react";
import {
  FormLabel,
  FormHelperText,
  Stack,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { documentBuilderService } from "../../services/documentBuilderService";

interface FileInputProps {
  label: string;
  value: string[]; // URLs
  onChange: (urls: string[]) => void;
  multiple?: boolean;
  error?: string;
  folderPath: string;
  resizeTo50?: boolean;
}

export const ControlledFileInput: React.FC<FileInputProps> = ({
  label,
  value,
  onChange,
  multiple = false,
  error,
  folderPath,
  resizeTo50,
}) => {
  const inputId = React.useId();
  const [uploading, setUploading] = React.useState(false);

  const resizeImageTo50 = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = () => {
        canvas.width = 60;
        canvas.height = 60;

        ctx?.drawImage(img, 0, 0, 50, 50);

        canvas.toBlob((blob) => {
          if (!blob) return resolve(file);

          const newFile = new File([blob], file.name, {
            type: file.type,
          });

          resolve(newFile);
        }, file.type);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (files: FileList | null) => {
    if (!files) return;

    try {
      setUploading(true);

      let fileArray = Array.from(files);

      if (resizeTo50) {
        fileArray = await Promise.all(
          fileArray.map((file) => {
            if (!file.type.startsWith("image/")) return file;
            return resizeImageTo50(file);
          }),
        );
      }

      const res = await documentBuilderService.uploadDocuments(
        fileArray,
        folderPath,
      );

      const uploadedUrls = res?.documents?.map((file: any) => file.id) || [];

      //  THIS IS IMPORTANT
      const updatedValue = multiple
        ? [...(value || []), ...uploadedUrls]
        : uploadedUrls;

      onChange(updatedValue);
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploading(false);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <Stack spacing={1} sx={{ my: 2 }}>
      <FormLabel error={!!error}>{label}</FormLabel>

      {/* Upload Button */}
      <Box display="flex" alignItems="center" gap={1}>
        <label htmlFor={inputId} style={{ flexGrow: 1 }}>
          <input
            id={inputId}
            type="file"
            multiple={multiple}
            onChange={(e) => handleFileChange(e.target.files)}
            style={{ display: "none" }}
          />

          <Button
            variant="outlined"
            component="span"
            fullWidth
            startIcon={<AttachFileIcon />}
            color={error ? "error" : "primary"}
            disabled={uploading}
            sx={{ justifyContent: "flex-start" }}
          >
            {uploading
              ? "Uploading..."
              : value?.length > 0
                ? `${value.length} file${value.length > 1 ? "s" : ""} selected`
                : "Choose file(s)"}
          </Button>
        </label>

        {/* Clear All */}
        {value && value.length > 0 && (
          <IconButton color="error" size="small" onClick={handleClearAll}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Stack>
  );
};
